"use client";
import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";

// local components imports
import CustomImage from "../../../../../components/atoms/customImage";
import VideoPlayer from "../../../../../components/atoms/videoPlayer";
import RivePlayer from "../../../../../components/atoms/rivePlayer";
import MobileSearch from "../../../../../components/organisms/mobileSearch";
import Header from "../../../../../components/organisms/header";
import BrandTabs from "./BrandTabs";
import MastHeadLottie from "./MastHeadLottie";
import Widget from "../../Widget";

// services and utils imports
import { MEDIA_TYPE } from "../../../../../constants/pageConstants/dynamicHomeConstants";

/**
 * MastHead component renders the mobile masthead section with header,
 * brand tabs, background media (video, lottie, image, color), and curated widgets.
 * Header scrolls with the masthead until it reaches the viewport top, then pins sticky.
 * @param {Object} props - Component props
 * @param {Array} props.mastHeadData - Array of store/brand data
 * @returns {JSX.Element} Rendered masthead
 */
const MastHead = ({ mastHeadData, currencyListWithHeaders }) => {
  // Get selected brand — identified by isSelected flag set in Redux by selectMobileStore reducer
  const selectedBrand = mastHeadData?.find((s) => s.isSelected) ?? mastHeadData?.[0];
  const mastHeadBg = selectedBrand?.mastHeadBg;
  const padding = selectedBrand?.storeStyle?.stripPaddingBottomPx ?? 10;
  const headerTextIconColor = selectedBrand?.storeStyle?.previewHeaderTextIconColor;

  const headerSentinelRef = useRef(null);
  const headerWrapRef = useRef(null);
  const [isHeaderPinned, setIsHeaderPinned] = useState(false);
  const [headerHeight, setHeaderHeight] = useState(0);

  // Media type checks
  const isVideo = mastHeadBg?.mediaType === MEDIA_TYPE.VIDEO;
  const isLottie = mastHeadBg?.mediaType === MEDIA_TYPE.LOTTIE;
  const isRive = mastHeadBg?.mediaType === MEDIA_TYPE.RIVE;
  const isImage = mastHeadBg?.mediaType === MEDIA_TYPE.IMAGE;
  const isColor = mastHeadBg?.mediaType === MEDIA_TYPE.COLOR;
  const isGradient = mastHeadBg?.mediaType === MEDIA_TYPE.GRADIENT;

  // Animate the fade only when the shopper switches brand tabs — never on the
  // first paint, otherwise the SSR-rendered masthead blinks out and re-fades in
  // after hydration.
  const previousStoreIdRef = useRef(selectedBrand?.storeId);
  const hasMountedRef = useRef(false);
  const isBrandSwitch = hasMountedRef.current && previousStoreIdRef.current !== selectedBrand?.storeId;

  useEffect(() => {
    hasMountedRef.current = true;
    previousStoreIdRef.current = selectedBrand?.storeId;
  }, [selectedBrand?.storeId]);

  useEffect(() => {
    const sentinel = headerSentinelRef.current;
    const headerWrap = headerWrapRef.current;
    if (!sentinel || !headerWrap) {
      return undefined;
    }

    /**
     * Keeps measured header height in sync for the pinned-state spacer.
     */
    const syncHeaderHeight = () => {
      setHeaderHeight(headerWrap.offsetHeight || 0);
    };

    /**
     * Pins the header once its in-flow top (sentinel) reaches or passes the viewport top.
     */
    const syncPinnedState = () => {
      setIsHeaderPinned(sentinel.getBoundingClientRect().top <= 0);
    };

    syncHeaderHeight();
    syncPinnedState();

    const resizeObserver = new ResizeObserver(() => {
      syncHeaderHeight();
      syncPinnedState();
    });
    resizeObserver.observe(headerWrap);

    window.addEventListener("scroll", syncPinnedState, { passive: true });
    window.addEventListener("resize", syncPinnedState, { passive: true });

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("scroll", syncPinnedState);
      window.removeEventListener("resize", syncPinnedState);
    };
  }, [selectedBrand?.storeId]);

  /**
   * Returns the CSS background for the masthead. For media backgrounds
   * (image/video/lottie/rive) it paints the brand's base color immediately so
   * there is no white flash before the high-priority media asset loads.
   * @returns {{ background: string }} CSS background style
   */
  const getBackgroundStyle = () => {
    if (isColor || isGradient) {
      return { background: mastHeadBg.mediaValue };
    }
    const baseColor = mastHeadBg?.fallbackColor || selectedBrand?.storeStyle?.background?.active;
    return { background: baseColor || "#ffffff" };
  };

  return (
    <>
      <div
        className="relative block w-full overflow-x-hidden transition-colors duration-500 ease-in-out md:hidden"
        style={{ ...getBackgroundStyle() }}
      >
        {/* Grid with both children in the same cell — height = max(media, content) */}
        <div className="grid w-full">
          <div className="w-full" style={{ gridArea: "1/1" }}>
            {isImage && mastHeadBg?.mediaValue && (
              <CustomImage
                src={mastHeadBg.mediaValue}
                alt={mastHeadBg?.alt || `${selectedBrand?.storeName || selectedBrand?.name || "FNP"} homepage banner`}
                className="h-full w-full object-cover"
                fill={true}
                isPriority={true}
              />
            )}

            {isVideo && mastHeadBg?.mediaValue && (
              <VideoPlayer
                videoUrl={mastHeadBg.mediaValue}
                classes="h-full w-full object-cover"
                isAutoPlay={true}
                isLoop={true}
                isMuted={true}
                isPlaysInline={true}
                preload="auto"
              />
            )}

            {isLottie && mastHeadBg?.mediaValue && <MastHeadLottie src={mastHeadBg.mediaValue} />}

            {isRive && mastHeadBg?.mediaValue && <RivePlayer src={mastHeadBg.mediaValue} classes="h-full w-full" />}
          </div>

          {/* Content — overlaid in same grid cell, also contributes to height */}
          <div className="w-full max-w-[100vw]" style={{ gridArea: "1/1" }}>
            {/* Sentinel marks the header's in-flow top; when it leaves the viewport, pin. */}
            <div ref={headerSentinelRef} className="pointer-events-none h-0 w-full" aria-hidden="true" />
            <div style={isHeaderPinned ? { height: headerHeight } : undefined}>
              <div ref={headerWrapRef} className={isHeaderPinned ? "fixed inset-x-0 top-0 z-50" : "relative z-50"}>
                <Header
                  isMobile={true}
                  isTransparentOnMobile={true}
                  isTopBanner={false}
                  headerTextIconColor={headerTextIconColor}
                  catalogId="india"
                  currencyListWithHeaders={currencyListWithHeaders}
                />
              </div>
            </div>

            <div className="relative top-0 bg-transparent pl-[15px] pr-[15px]">
              <MobileSearch
                catalogId={"india"}
                searchBarStyles={{ zIndex: "10", position: "relative", display: "flex" }}
                inputClasses="!h-[42px] xxs:!h-[44px] xxsplus:!h-[46px] xxsmax:!h-[48px]"
                isGiftFinderEnabled={true}
              />
            </div>
            {mastHeadData?.length > 1 ? <BrandTabs mastHeadData={mastHeadData} /> : null}

            <div
              key={selectedBrand?.storeId ?? 0}
              className={isBrandSwitch ? "animate-fadeIn" : ""}
              style={{ paddingTop: `${isLottie ? 0 : padding}px` }}
            >
              {selectedBrand?.curatedWidgets?.map((widgetData, index) => (
                <Widget
                  key={widgetData.widgetId ?? index}
                  order={index + 1}
                  widget={widgetData}
                  isMasthead={true}
                  storeName={selectedBrand?.storeName}
                  isMobile={true}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

MastHead.propTypes = {
  mastHeadData: PropTypes.arrayOf(PropTypes.object),
  currencyListWithHeaders: PropTypes.object,
};

export default MastHead;
